/**
 * MIRHA MILANO PRESTIGE — Theme JavaScript
 * Performance-optimized, deferred loading.
 * Handles: scroll animations, sticky header, cart drawer, product interactions,
 *          quantity selectors, tabs, mobile menu, and collection sorting.
 */

(function () {
  'use strict';

  /* ============================================
     SCROLL ANIMATIONS (Intersection Observer)
     ============================================ */
  const initAnimations = () => {
    if (document.body.dataset.animationsEnabled !== 'true') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            // Also animate children in stagger containers
            if (entry.target.classList.contains('animate-stagger')) {
              entry.target.querySelectorAll('.animate-fade-up').forEach((child) => {
                child.classList.add('is-visible');
              });
            }
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -60px 0px',
      }
    );

    document.querySelectorAll('[data-animate], .animate-fade-up, .animate-fade-in, .animate-scale-in').forEach((el) => {
      observer.observe(el);
    });
  };

  /* ============================================
     STICKY HEADER
     ============================================ */
  const initStickyHeader = () => {
    const header = document.getElementById('site-header');
    if (!header) return;

    let lastScroll = 0;
    const scrollThreshold = 50;

    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (currentScroll > scrollThreshold) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
      lastScroll = currentScroll;
    };

    // Use passive listener for performance (INP optimization)
    window.addEventListener('scroll', handleScroll, { passive: true });
  };

  /* ============================================
     CART DRAWER
     ============================================ */
  const initCartDrawer = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;

    const openCart = () => {
      cartDrawer.classList.add('is-open');
      cartDrawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const closeCart = () => {
      cartDrawer.classList.remove('is-open');
      cartDrawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    // Open cart
    document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
      btn.addEventListener('click', openCart);
    });

    // Close cart
    document.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', closeCart);
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer.classList.contains('is-open')) {
        closeCart();
      }
    });
  };

  /* ============================================
     MOBILE MENU
     ============================================ */
  const initMobileMenu = () => {
    const mobileMenu = document.getElementById('mobile-menu');
    const overlay = document.getElementById('page-overlay');
    if (!mobileMenu) return;

    const openMenu = () => {
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    };

    const closeMenu = () => {
      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    };

    document.querySelectorAll('[data-mobile-menu-toggle]').forEach((btn) => {
      btn.addEventListener('click', openMenu);
    });

    document.querySelectorAll('[data-mobile-menu-close]').forEach((btn) => {
      btn.addEventListener('click', closeMenu);
    });

    if (overlay) {
      overlay.addEventListener('click', closeMenu);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
        closeMenu();
      }
    });

    // Toggle mobile nested submenus
    mobileMenu.querySelectorAll('[data-submenu-toggle]').forEach((toggleBtn) => {
      toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const wrapper = toggleBtn.closest('.mobile-menu__link-wrapper');
        const submenu = wrapper.nextElementSibling;

        if (submenu) {
          const isOpen = submenu.style.display === 'block';
          submenu.style.display = isOpen ? 'none' : 'block';
          toggleBtn.classList.toggle('is-active', !isOpen);
          toggleBtn.setAttribute('aria-expanded', !isOpen);
        }
      });
    });
  };

  /* ============================================
     QUANTITY SELECTOR
     ============================================ */
  const initQuantitySelectors = () => {
    document.querySelectorAll('[data-quantity-minus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('.quantity-selector__input');
        const currentVal = parseInt(input.value, 10) || 1;
        if (currentVal > 1) {
          input.value = currentVal - 1;
          input.dispatchEvent(new Event('change'));
        }
      });
    });

    document.querySelectorAll('[data-quantity-plus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('.quantity-selector__input');
        const currentVal = parseInt(input.value, 10) || 1;
        const max = parseInt(input.max, 10) || 99;
        if (currentVal < max) {
          input.value = currentVal + 1;
          input.dispatchEvent(new Event('change'));
        }
      });
    });
  };

  /* ============================================
     PRODUCT TABS
     ============================================ */
  const initProductTabs = () => {
    const accordions = document.querySelectorAll('[data-accordion-group] .accordion__item');
    
    accordions.forEach((item) => {
      const trigger = item.querySelector('[data-accordion-toggle]');
      if (!trigger) return;

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = item.classList.contains('is-open');
        item.classList.toggle('is-open', !isOpen);
        trigger.setAttribute('aria-expanded', !isOpen);
      });
    });
  };

  /* ============================================
     PRODUCT GALLERY (Thumbnail clicks)
     ============================================ */
  const initProductGallery = () => {
    const thumbs = document.querySelectorAll('.product-gallery__thumb');
    const mainImage = document.getElementById('product-featured-image');
    if (!mainImage || thumbs.length === 0) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const newSrc = thumb.dataset.imageUrl;
        const newSrcset = thumb.dataset.imageSrcset;
        const newAlt = thumb.dataset.imageAlt;

        if (newSrc) {
          mainImage.style.opacity = '0.5';
          setTimeout(() => {
            mainImage.src = newSrc;
            if (newSrcset) mainImage.srcset = newSrcset;
            mainImage.alt = newAlt || '';
            mainImage.style.opacity = '1';
          }, 100);
        }

        thumbs.forEach((t) => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  };

  /* ============================================
     VARIANT SELECTOR
     ============================================ */
  const initVariantSelector = () => {
    const productJson = document.getElementById('product-json');
    if (!productJson) return;

    let product;
    try {
      product = JSON.parse(productJson.textContent);
    } catch (e) {
      return;
    }

    const variantButtons = document.querySelectorAll('.variant-btn');
    const variantInput = document.getElementById('product-variant-id');
    const addToCartBtn = document.getElementById('add-to-cart-btn');

    variantButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const optionName = btn.dataset.optionName;
        const optionValue = btn.dataset.optionValue;

        // Update button states for this option group
        btn.closest('.variant-buttons').querySelectorAll('.variant-btn').forEach((b) => {
          b.classList.remove('selected');
        });
        btn.classList.add('selected');

        // Update selected label
        const selectedLabel = document.getElementById(`selected-${optionName.toLowerCase().replace(/\s+/g, '-')}`);
        if (selectedLabel) selectedLabel.textContent = optionValue;

        // Find matching variant
        const selectedOptions = [];
        document.querySelectorAll('.variant-btn.selected').forEach((selected) => {
          selectedOptions.push(selected.dataset.optionValue);
        });

        const matchingVariant = product.variants.find((v) => {
          return v.options.every((opt, idx) => opt === selectedOptions[idx]);
        });

        if (matchingVariant && variantInput) {
          variantInput.value = matchingVariant.id;

          // Update price
          const priceEl = document.querySelector('.product-info__price-current');
          if (priceEl) {
            priceEl.textContent = formatMoney(matchingVariant.price);
          }

          // Update availability
          if (addToCartBtn) {
            if (matchingVariant.available) {
              addToCartBtn.disabled = false;
              addToCartBtn.textContent = addToCartBtn.dataset.addText || 'Add to Cart';
            } else {
              addToCartBtn.disabled = true;
              addToCartBtn.textContent = addToCartBtn.dataset.soldOutText || 'Sold Out';
            }
          }

          // Update URL
          const url = new URL(window.location);
          url.searchParams.set('variant', matchingVariant.id);
          window.history.replaceState({}, '', url);
        }
      });
    });
  };

  /* ============================================
     QUICK ADD TO CART (AJAX)
     ============================================ */
  const initQuickAdd = () => {
    document.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = btn.dataset.addToCart;
        const originalText = btn.textContent;

        btn.textContent = '✓ Added';
        btn.disabled = true;

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: parseInt(variantId, 10), quantity: 1 }],
            }),
          });

          if (response.ok) {
            // Update cart count
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            document.querySelectorAll('[data-cart-count]').forEach((el) => {
              el.textContent = cart.item_count;
              el.style.display = '';
            });

            // Announce to screen readers
            const liveRegion = document.getElementById('live-region');
            if (liveRegion) liveRegion.textContent = 'Item added to cart';
          }
        } catch (err) {
          console.error('Failed to add to cart:', err);
        }

        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      });
    });
  };

  /* ============================================
     COLLECTION SORTING
     ============================================ */
  const initCollectionSort = () => {
    const sortSelect = document.querySelector('[data-collection-sort]');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  };

  /* ============================================
     HELPER: Format Money
     ============================================ */
  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    // Detect currency from existing price display
    const existingPrice = document.querySelector('.product-info__price-current');
    if (existingPrice) {
      const text = existingPrice.textContent.trim();
      if (text.includes('€')) return `€${amount}`;
      if (text.includes('$')) return `$${amount}`;
      if (text.includes('£')) return `£${amount}`;
    }
    return `€${amount}`;
  };

  /* ============================================
     HEADER DROPDOWN MENUS
     ============================================ */
  const initDropdowns = () => {
    const dropdownItems = document.querySelectorAll('.header__nav-item.has-dropdown');

    dropdownItems.forEach((item) => {
      const link = item.querySelector('.header__nav-link');
      const dropdown = item.querySelector('.header__dropdown');
      if (!dropdown) return;

      let closeTimer;

      const openDropdown = () => {
        window.clearTimeout(closeTimer);
        item.classList.add('is-open');
        if (link) link.setAttribute('aria-expanded', 'true');
      };

      const scheduleClose = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          item.classList.remove('is-open');
          if (link) link.setAttribute('aria-expanded', 'false');
        }, 450);
      };

      item.addEventListener('mouseenter', openDropdown);
      item.addEventListener('mouseleave', scheduleClose);
      item.addEventListener('focusin', openDropdown);
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) scheduleClose();
      });
      dropdown.addEventListener('mouseenter', openDropdown);
    });
  };

  const initPredictiveSearch = () => {
    const searchOverlay = document.getElementById('search-overlay');
    const input = document.getElementById('search-overlay-input');
    const resultsContainer = document.getElementById('search-overlay-results');
    if (!searchOverlay || !input) return;

    const openSearch = () => {
      searchOverlay.classList.add('is-open');
      searchOverlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input.focus(), 100);
    };

    const closeSearch = () => {
      searchOverlay.classList.remove('is-open');
      searchOverlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    document.querySelectorAll('[data-search-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openSearch();
      });
    });

    document.querySelectorAll('[data-search-close]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        closeSearch();
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchOverlay.classList.contains('is-open')) {
        closeSearch();
      }
    });

    // Predictive search API query on input
    let debounceTimer;
    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const query = input.value.trim();

      if (query.length < 3) {
        resultsContainer.innerHTML = '';
        return;
      }

      debounceTimer = setTimeout(() => {
        fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4`)
          .then((res) => res.json())
          .then((data) => {
            const products = data.resources.results.products;
            if (!products || products.length === 0) {
              resultsContainer.innerHTML = '<p style="padding: 16px 0; color: var(--color-text-light);">No products found.</p>';
              return;
            }

            let html = `
              <div class="predictive-search-results__title">Suggested Products</div>
              <div class="predictive-search-results__grid">
            `;

            products.forEach((product) => {
              html += `
                <a href="${product.url}" class="predictive-search-results__item">
                  <img class="predictive-search-results__image" src="${product.image || 'https://cdn.shopify.com/s/images/themes/product-1.png'}" alt="${product.title}">
                  <div class="predictive-search-results__info">
                    <h4 class="predictive-search-results__item-title">${product.title}</h4>
                    <span class="predictive-search-results__item-price">${product.price_formatted || ''}</span>
                  </div>
                </a>
              `;
            });

            html += `</div>`;
            resultsContainer.innerHTML = html;
          })
          .catch((err) => {
             console.error('Predictive search error:', err);
          });
      }, 300);
    });
  };

  const initParallax = () => {
    const heroImage = document.querySelector('.hero-banner:not(.hero-banner--split) .hero-banner__media img, .slideshow__slide-media img');
    if (!heroImage) return;

    window.addEventListener('scroll', () => {
      const scrollPos = window.scrollY;
      if (scrollPos < window.innerHeight) {
        heroImage.style.transform = `translate3d(0, ${scrollPos * 0.35}px, 0)`;
      }
    }, { passive: true });
  };

  const initTestimonialSlider = () => {
    const slider = document.querySelector('[data-testimonials-slider]');
    if (!slider) return;

    const slides = slider.querySelectorAll('[data-testimonial-slide]');
    const dots = document.querySelectorAll('[data-testimonial-dot]');
    if (slides.length <= 1) return;

    let currentIndex = 0;

    const showSlide = (index) => {
      slides.forEach((slide, idx) => {
        slide.classList.toggle('is-active', idx === index);
      });
      dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === index);
      });
      currentIndex = index;
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.getAttribute('data-testimonial-dot'), 10);
        showSlide(index);
      });
    });

    // Auto rotate every 7 seconds
    setInterval(() => {
      let nextIndex = currentIndex + 1;
      if (nextIndex >= slides.length) nextIndex = 0;
      showSlide(nextIndex);
    }, 7000);
  };

  const initSlideshows = () => {
    const slideshows = document.querySelectorAll('.slideshow');
    slideshows.forEach((slideshow) => {
      const slides = slideshow.querySelectorAll('.slideshow__slide');
      const dots = slideshow.querySelectorAll('.slideshow__dot');
      const prevBtn = slideshow.querySelector('.slideshow__arrow--prev');
      const nextBtn = slideshow.querySelector('.slideshow__arrow--next');
      if (slides.length <= 1) return;

      let currentIndex = 0;
      let autoplayTimer = null;
      const autoplaySpeed = parseInt(slideshow.dataset.autoplaySpeed, 10) || 5000;
      const autoplay = slideshow.dataset.autoplay === 'true';

      const showSlide = (index) => {
        slides.forEach((slide, idx) => {
          slide.classList.toggle('is-active', idx === index);
        });
        dots.forEach((dot, idx) => {
          dot.classList.toggle('is-active', idx === index);
        });
        currentIndex = index;
      };

      const nextSlide = () => {
        let nextIdx = currentIndex + 1;
        if (nextIdx >= slides.length) nextIdx = 0;
        showSlide(nextIdx);
      };

      const prevSlide = () => {
        let prevIdx = currentIndex - 1;
        if (prevIdx < 0) prevIdx = slides.length - 1;
        showSlide(prevIdx);
      };

      if (nextBtn) nextBtn.addEventListener('click', () => { nextSlide(); resetAutoplay(); });
      if (prevBtn) prevBtn.addEventListener('click', () => { prevSlide(); resetAutoplay(); });

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.getAttribute('data-index'), 10);
          showSlide(index);
          resetAutoplay();
        });
      });

      const startAutoplay = () => {
        if (autoplay) {
          autoplayTimer = setInterval(nextSlide, autoplaySpeed);
        }
      };

      const resetAutoplay = () => {
        if (autoplayTimer) {
          clearInterval(autoplayTimer);
        }
        startAutoplay();
      };

      startAutoplay();
    });
  };

  /* ============================================
     CATEGORY PRODUCT SLIDERS (Drag + Arrows)
     ============================================ */
  const initCategorySliders = () => {
    document.querySelectorAll('[data-category-slider]').forEach((section) => {
      const track = section.querySelector('[data-slider-track]');
      const prevBtn = section.querySelector('[data-slider-prev]');
      const nextBtn = section.querySelector('[data-slider-next]');
      if (!track) return;

      // Arrow navigation
      const scrollAmount = () => {
        const slide = track.querySelector('.category-slider__slide');
        return slide ? slide.offsetWidth + 24 : 300;
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
        });
      }

      // Mouse drag scrolling
      let isDown = false;
      let startX;
      let scrollLeft;

      track.addEventListener('mousedown', (e) => {
        isDown = true;
        track.classList.add('is-dragging');
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
      });

      track.addEventListener('mouseleave', () => {
        isDown = false;
        track.classList.remove('is-dragging');
      });

      track.addEventListener('mouseup', () => {
        isDown = false;
        track.classList.remove('is-dragging');
      });

      track.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - track.offsetLeft;
        const walk = (x - startX) * 1.5;
        track.scrollLeft = scrollLeft - walk;
      });
    });
  };

  /* ============================================
     GOOGLE REVIEWS SLIDER (Drag scroll)
     ============================================ */
  const initGoogleReviewsSlider = () => {
    document.querySelectorAll('[data-google-reviews-slider]').forEach((track) => {
      let isDown = false;
      let startX;
      let scrollLeft;

      track.addEventListener('mousedown', (e) => {
        isDown = true;
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
        track.style.cursor = 'grabbing';
      });

      track.addEventListener('mouseleave', () => {
        isDown = false;
        track.style.cursor = '';
      });

      track.addEventListener('mouseup', () => {
        isDown = false;
        track.style.cursor = '';
      });

      track.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - track.offsetLeft;
        const walk = (x - startX) * 1.5;
        track.scrollLeft = scrollLeft - walk;
      });
    });
  };

  /* ============================================
     INITIALIZATION
     ============================================ */
  const init = () => {
    initAnimations();
    initStickyHeader();
    initCartDrawer();
    initMobileMenu();
    initQuantitySelectors();
    initProductTabs();
    initProductGallery();
    initVariantSelector();
    initQuickAdd();
    initCollectionSort();
    initDropdowns();
    initPredictiveSearch();
    initParallax();
    initTestimonialSlider();
    initSlideshows();
    initCategorySliders();
    initGoogleReviewsSlider();
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
